import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import { Input } from '@comeon/ui';
import { selectLocale } from '~/components/account/ducks/index.js';
import Icon, { ICON_PLANE, ICON_TOP, ICON_BOTTOM, ICON_SUPPORT_LINE } from '~/components/icon/index.js';
import useTranslations from '~/components/translation/use-translations.ts';
import {
	importZendesk,
	setDepartment,
	getDepartmentStatus,
	setChatTags,
	removeChatTags,
	suppressChat,
	suppressHelpCenter,
	setHelpCenterSuggestions,
	toggleContactOptions,
	getDefaultDepartment,
	showSupportDialogBox
} from '~/components/support/ducks/index.js';
import { selectSiteCode } from '~/components/brand/ducks/selectors.js';

import formatLocaleForZendeskDepartment from '~/utils/format-locale-for-zendesk.js';

const DeptOnlineInner = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
`;

const ChatInputWrapper = styled.div`
	display: flex;
	align-items: center;
`;

const Submit = styled.button`
	background-color: rgba(var(--primary));
	width: 34px;
	height: 34px;
	padding: 5px;
	border-radius: 20px;
`;

const selectSupport = state => state.support;
const textKeys = [
	'SUPPORT_LOADING_SUPPORT',
	'PROACTIVE_CHAT_DEP_ONLINE_AGENT_READY',
	'SUPPORT_PROACTIVE_CHAT_DEP_OFFLINE',
	'PROACTIVE_CHAT_DEP_ONLINE_QUESTION',
	'PROACTIVE_CHAT_DEP_ONLINE_QUESTION_PLACEHOLDER'
];

const ContactZendeskAgent = ({ department, chatTags, searchLabels, className, callbackFunction }) => {
	const [deptOnline, setDeptOnline] = useState(false);
	const [showChatWrapper, setShowChatWrapper] = useState(false);
	const [chatMessage, setChatMessage] = useState('');

	const siteCode = useSelector(selectSiteCode);
	const locale = useSelector(selectLocale);
	const { supportLoading } = useSelector(selectSupport);

	const formattedLocale = formatLocaleForZendeskDepartment(locale);
	const defaultDept = getDefaultDepartment(siteCode, formattedLocale);
	const targetDept = department ? `${defaultDept} ${department}` : defaultDept;

	const {
		isLoadingTranslations,
		translations: {
			SUPPORT_LOADING_SUPPORT,
			PROACTIVE_CHAT_DEP_ONLINE_AGENT_READY,
			SUPPORT_PROACTIVE_CHAT_DEP_OFFLINE,
			PROACTIVE_CHAT_DEP_ONLINE_QUESTION,
			PROACTIVE_CHAT_DEP_ONLINE_QUESTION_PLACEHOLDER
		}
	} = useTranslations(textKeys);

	const dispatch = useDispatch();

	useEffect(() => {
		dispatch(
			importZendesk(() => {
				const deptStatus = getDepartmentStatus(targetDept);

				setDeptOnline(deptStatus === 'online');
				if (window?.dataLayer) {
					window.dataLayer.push({
						event: `trackEvent`,
						eventAction: `View - Proactive chat - ${deptStatus === 'online' ? 'Online' : 'Offline'}`,
						eventCategory: `Sidebar`,
						eventLabel: targetDept
					});
				}
			})
		);
	}, [dispatch, targetDept]);

	const deptOnlineHandler = () => {
		setDepartment(targetDept);
		setChatTags(chatTags);
		suppressHelpCenter(true);
		toggleContactOptions(false);
		suppressChat(false);
		window.zE('webWidget:on', 'chat:end', () => {
			// when the chat has finished
			// cleanup the stuff we set up
			setDepartment(defaultDept);
			removeChatTags(chatTags);
			suppressHelpCenter(false);
			toggleContactOptions(true);
		});
	};

	const deptOfflineHandler = () => {
		suppressHelpCenter(false);
		toggleContactOptions(true);
		setHelpCenterSuggestions({
			url: true,
			labels: [...searchLabels]
		});
		if (window?.dataLayer) {
			window.dataLayer.push({
				event: `trackEvent`,
				eventAction: `Click - Proactive chat - Offline`,
				eventCategory: `Sidebar`,
				eventLabel: targetDept
			});
		}
	};

	if (isLoadingTranslations) return null;

	if (supportLoading) {
		return (
			<button className={`btn ${className ?? 'btn--secondary'}`} disabled>
				<span>{SUPPORT_LOADING_SUPPORT}</span>
				<div className="spinner" />
			</button>
		);
	}

	if (!deptOnline) {
		return (
			<button
				onClick={e => {
					e.stopPropagation();
					e.preventDefault();
					deptOfflineHandler();
					dispatch(showSupportDialogBox(true));
					// Call the callback function if it exists
					if (callbackFunction) {
						callbackFunction();
					}
				}}
				className={`btn ${className ?? 'btn--secondary'}`}
			>
				{SUPPORT_PROACTIVE_CHAT_DEP_OFFLINE}
			</button>
		);
	}

	if (showChatWrapper) {
		return (
			<div className={`btn ${className ?? 'btn--secondary'}`}>
				<div>
					<DeptOnlineInner>
						<Icon type={ICON_SUPPORT_LINE} />
						<span>{PROACTIVE_CHAT_DEP_ONLINE_QUESTION}</span>
						<Icon type={ICON_BOTTOM} />
					</DeptOnlineInner>
				</div>
				<ChatInputWrapper>
					<Input
						placeholder={PROACTIVE_CHAT_DEP_ONLINE_QUESTION_PLACEHOLDER}
						value={chatMessage}
						onChange={e => {
							setChatMessage(e.target.value);
						}}
					/>
					<Submit
						onClick={e => {
							e.stopPropagation();
							e.preventDefault();
							deptOnlineHandler();
							window.zE('webWidget', 'chat:send', chatMessage);
							dispatch(showSupportDialogBox(true));
							// Call the callback function if it exists
							if (callbackFunction) {
								callbackFunction();
							}
							if (window?.dataLayer) {
								window.dataLayer.push({
									event: `trackEvent`,
									eventAction: `Sent - Proactive chat - Online`,
									eventCategory: `Sidebar`,
									eventLabel: targetDept
								});
							}
						}}
					>
						<Icon type={ICON_PLANE} />
					</Submit>
				</ChatInputWrapper>
			</div>
		);
	}

	if (deptOnline) {
		return (
			<button
				onClick={e => {
					e.stopPropagation();
					e.preventDefault();
					setShowChatWrapper(true);
					// Call the callback function if it exists
					if (callbackFunction) {
						callbackFunction();
					}
					if (window?.dataLayer) {
						window.dataLayer.push({
							event: `trackEvent`,
							eventAction: `Click - Proactive chat - Online`,
							eventCategory: `Sidebar`,
							eventLabel: targetDept
						});
					}
				}}
				className={`btn ${className ?? 'btn--secondary'}`}
			>
				<DeptOnlineInner>
					<Icon type={ICON_SUPPORT_LINE} />
					<span>{PROACTIVE_CHAT_DEP_ONLINE_AGENT_READY}</span>
					<Icon type={ICON_TOP} />
				</DeptOnlineInner>
			</button>
		);
	}

	return null;
};

export default ContactZendeskAgent;
